import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import {
  useApproveAuthorization,
  useAuthorizationStatus,
  useCancelAuthorization,
  useMe,
  useMyPermissions,
  useRejectAuthorization,
} from '../../api/queries';
import type { AuthorizationListRow } from '../../api/types';
import { fmtDateTime } from '../../utils/format';

export type DecisionDialogProps = {
  request: AuthorizationListRow | null;
  onClose: () => void;
};

/**
 * Decision dialog for an authorization request. Shows the requirement
 * progress, the payload, and (when current user can act) Approve / Reject
 * buttons with a free-text Notes field. The requester sees Cancel.
 */
export function DecisionDialog({ request, onClose }: DecisionDialogProps) {
  const open = request !== null;
  const status = useAuthorizationStatus(request?.id ?? null);
  const me = useMe();
  const perms = useMyPermissions();
  const has = (code: string) => perms.data?.codes?.includes(code) ?? false;

  const approve = useApproveAuthorization(request?.id ?? '00000000-0000-0000-0000-000000000000');
  const reject  = useRejectAuthorization(request?.id ?? '00000000-0000-0000-0000-000000000000');
  const cancel  = useCancelAuthorization(request?.id ?? '00000000-0000-0000-0000-000000000000');

  const [notes, setNotes] = useState('');
  const [mfaFactor, setMfaFactor] = useState('totp');

  useEffect(() => {
    if (!open) {
      setNotes('');
      setMfaFactor('totp');
      approve.reset();
      reject.reset();
      cancel.reset();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, request?.id]);

  const isMine = request?.requesterUserId === me.data?.user_id;
  const isPending = status.data?.state === 'pending';
  const canApprove = isPending && !isMine && has('authorizations.approve');
  const canReject  = isPending && !isMine && has('authorizations.reject');
  const canCancel  = isPending && isMine;

  const busy = approve.isPending || reject.isPending || cancel.isPending;

  const onApprove = () => {
    if (!mfaFactor.trim()) return;
    approve.mutate({ mfaFactor, notes: notes || null }, {
      onSuccess: () => onClose(),
    });
  };
  const onReject = () => {
    reject.mutate({ notes: notes || null }, { onSuccess: () => onClose() });
  };
  const onCancel = () => {
    if (!confirm('Cancel this request?')) return;
    cancel.mutate(undefined, { onSuccess: () => onClose() });
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Stack>
          <Typography variant="h6">Authorization request</Typography>
          {request && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {request.actionClass}
            </Typography>
          )}
        </Stack>
        <IconButton size="small" onClick={onClose} disabled={busy}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {status.isLoading ? (
          <Stack direction="row" spacing={2}><CircularProgress size={20} /><Typography>Loading…</Typography></Stack>
        ) : status.error ? (
          <ErrorAlert>{(status.error as { message?: string }).message}</ErrorAlert>
        ) : status.data && request ? (
          <Stack spacing={3}>
            {/* Header bar */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <StateChip state={status.data.state} />
                <Typography variant="body2" color="text.secondary">
                  Submitted {fmtDateTime(status.data.submittedAt)}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {isPending
                    ? `Expires ${fmtDateTime(status.data.expiresAt)}`
                    : status.data.resolvedAt
                      ? `Resolved ${fmtDateTime(status.data.resolvedAt)}`
                      : ''}
                </Typography>
              </Stack>
              {isPending && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={
                      status.data.requiredCount === 0
                        ? 100
                        : Math.min(100, (status.data.completedCount / status.data.requiredCount) * 100)
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {status.data.completedCount} of {status.data.requiredCount} approvals
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Identity */}
            <Box>
              <Typography variant="overline" color="text.secondary">Identity</Typography>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <DetailRow label="Action class" value={<code>{request.actionClass}</code>} />
                <DetailRow label="Requester" value={<code>{request.requesterUserId}</code>} />
                <DetailRow label="Operator" value={request.operatorId ?? '—'} />
                <DetailRow label="Target" value={
                  request.targetResourceType
                    ? `${request.targetResourceType} ${request.targetResourceId ?? ''}`
                    : '—'
                } />
                <DetailRow label="Idempotency key" value={request.idempotencyKey} />
              </Stack>
            </Box>

            <Divider />

            {/* Requirement progress */}
            <Box>
              <Typography variant="overline" color="text.secondary">Requirements</Typography>
              {status.data.requirements.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No active requirements for this action class — request was committed immediately.
                </Alert>
              ) : (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {status.data.requirements.map((req) => (
                    <Box key={req.requirementId}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={`Role: ${req.roleCode}`} />
                        <Typography variant="body2">
                          {req.completedCount} of {req.requiredCount} approvals
                        </Typography>
                      </Stack>
                      {req.decisions.length > 0 && (
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1, gap: 0.75 }}>
                          {req.decisions.map((d) => (
                            <Tooltip
                              key={`${d.approverUserId}-${d.decidedAt}`}
                              title={`Decided ${fmtDateTime(d.decidedAt)}; MFA: ${d.mfaFactorUsed}${d.decision === 'rejected' ? ' — REJECTED' : ''}`}
                            >
                              <Chip
                                size="small"
                                color={d.decision === 'approved' ? 'success' : 'error'}
                                variant="outlined"
                                label={`${d.decision} · ${d.approverUserId.slice(0, 8)}…`}
                              />
                            </Tooltip>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            {/* Decision area */}
            {(canApprove || canReject || canCancel) && (
              <>
                <Divider />
                <Box>
                  <Typography variant="overline" color="text.secondary">Decision</Typography>
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                      label="Notes (optional)"
                      multiline minRows={2}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      fullWidth
                    />
                    {(canApprove) && (
                      <TextField
                        label="MFA factor used"
                        helperText="totp, passkey, or push — required by UAC §12.4.4 to gate every approval"
                        value={mfaFactor}
                        onChange={(e) => setMfaFactor(e.target.value)}
                        fullWidth
                      />
                    )}
                    {(approve.error || reject.error || cancel.error) && (
                      <ErrorAlert>
                        {((approve.error || reject.error || cancel.error) as { message?: string })?.message}
                      </ErrorAlert>
                    )}
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {canCancel && (
            <Button color="warning" onClick={onCancel} disabled={busy}>
              Cancel request
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={busy}>Close</Button>
          {canReject && (
            <Button color="error" variant="outlined" onClick={onReject} disabled={busy}>
              {reject.isPending ? 'Rejecting…' : 'Reject'}
            </Button>
          )}
          {canApprove && (
            <Button color="primary" variant="contained" onClick={onApprove} disabled={busy || !mfaFactor.trim()}>
              {approve.isPending ? 'Approving…' : 'Approve'}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', columnGap: 2 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{value}</Typography>
    </Box>
  );
}

function StateChip({ state }: { state: string }) {
  const color =
    state === 'approved' || state === 'committed' ? 'success' :
    state === 'pending' ? 'warning' :
    state === 'rejected' ? 'error' :
    state === 'cancelled' || state === 'expired' ? 'default' : 'default';
  return <Chip size="small" label={state} color={color as 'success' | 'warning' | 'error' | 'default'} />;
}
